export const PLAYBACK_RATES = [0.5, 1, 1.5, 2] as const;

export const SEEK_STEP_SECONDS = 5;
export const FRAME_STEP_SECONDS = 1 / 24;
export const VOLUME_STEP = 0.1;

export interface BufferedSegment {
  left: number;
  width: number;
}

export function formatPlaybackTime(seconds: number | null | undefined): string {
  if (typeof seconds !== "number" || !Number.isFinite(seconds) || seconds < 0) return "0:00";
  const total = Math.floor(seconds);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  const paddedSecs = String(secs).padStart(2, "0");
  if (hours > 0) return `${hours}:${String(minutes).padStart(2, "0")}:${paddedSecs}`;
  return `${minutes}:${paddedSecs}`;
}

export function seekTargetFromRatio(ratio: number, duration: number | null | undefined): number {
  if (typeof duration !== "number" || !Number.isFinite(duration) || duration <= 0) return 0;
  if (!Number.isFinite(ratio)) return 0;
  return clamp(ratio, 0, 1) * duration;
}

export function offsetPlayhead(
  current: number,
  offset: number,
  duration: number | null | undefined
): number {
  const max = typeof duration === "number" && Number.isFinite(duration) && duration > 0 ? duration : 0;
  const next = (Number.isFinite(current) ? current : 0) + offset;
  return clamp(next, 0, max);
}

export function nextPlaybackRate(current: number): number {
  const index = PLAYBACK_RATES.findIndex((rate) => rate === current);
  return PLAYBACK_RATES[(index + 1) % PLAYBACK_RATES.length];
}

export function formatPlaybackRate(rate: number): string {
  return `${Number.isInteger(rate) ? rate : rate.toString()}×`;
}

export function bufferedSegments(
  ranges: readonly (readonly [number, number])[],
  duration: number | null | undefined
): BufferedSegment[] {
  if (typeof duration !== "number" || !Number.isFinite(duration) || duration <= 0) return [];
  return ranges
    .map(([start, end]) => [clamp(start, 0, duration), clamp(end, 0, duration)] as const)
    .filter(([start, end]) => end > start)
    .map(([start, end]) => ({
      left: (start / duration) * 100,
      width: ((end - start) / duration) * 100,
    }));
}

export function readBufferedRanges(buffered: TimeRanges | null | undefined): [number, number][] {
  if (!buffered) return [];
  const ranges: [number, number][] = [];
  for (let index = 0; index < buffered.length; index += 1) {
    ranges.push([buffered.start(index), buffered.end(index)]);
  }
  return ranges;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}
